import React, { useContext, useEffect, useState } from 'react';
import { IFaceRecognitionEventBaseProps } from '../../../services/Events/FaceRecognitionEventService/types';
import { Box } from '@mui/material';
import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import { getStreamByEndpointAsync } from '../../../services/Events';
import { FACE_RECOGNITION_EVENT_ENDPOINT } from '../../../services/Events/FaceRecognitionEventService';
import { IPaginationParams } from '../../../services/types';
import { AppContext } from '../../../context/AppContextProvider';
import { useGetAllFaceRecognitionEventCountByLicenseAsync } from '../../../services/Events/FaceRecognitionEventService/hooks/useMutation';
import ImageModalComponent from 'components/ImageModalComponent';
import ImageSimpleColumnComponent from '../../../components/ImageSimpleColumnComponent';
import { IEventPauseUpdateParams } from '../../../services/Events/types';
import {
  FACE_RECOGNITION_EVENT_CACHE_KEY,
  useFaceRecognitionEventDataStreamCache,
} from '../../../services/Events/FaceRecognitionEventService/hooks/useQueryClient';
import PauseButtonComponent from '../../../components/PauseButtonComponent';
import { t } from 'i18next';

const FaceEventsListPage = () => {
  const [events, setEvents] = useState<IFaceRecognitionEventBaseProps[]>([]);
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const { setIsShowLoadingComponent } = useContext(AppContext);
  const [eventCount, setEventCount] = useState<number>(0);
  const [isVisibleImageModal, setIsVisibleImageModal] =
    useState<boolean>(false);
  const [imageUrlToRender, setImageUrlToRender] = useState<string>('');
  const { mutateAsync: mutateGetAllFaceRecognitionEventByLicenseCount } =
    useGetAllFaceRecognitionEventCountByLicenseAsync();
  const { setCacheData, getCacheData, clearCacheData } =
    useFaceRecognitionEventDataStreamCache();
  const [pausedUpdateParams, setPausedUpdateParams] =
    useState<IEventPauseUpdateParams>({
      isPaused: false,
      cacheKey: FACE_RECOGNITION_EVENT_CACHE_KEY,
      setCacheData: setCacheData,
      getCacheData: getCacheData,
      clearCacheData: clearCacheData,
    });

  const columns: IColumnProps[] = [
    {
      id: 'device.name',
      label: t('common.device'),
      width: '20%',
    },
    { id: 'clientName', label: t('common.name'), width: 200 },
    {
      id: 'recognitionImage',
      label: t('common.image'),
      width: '15%',
      align: 'center',
      render: (url) => {
        return (
          <ImageSimpleColumnComponent
            url={url}
            onClick={() => handleOpenImageModal(url)}
          />
        );
      },
    },
    {
      id: 'image',
      label: t('common.image'),
      width: '15%',
      align: 'center',
      render: (url) => {
        return (
          <ImageSimpleColumnComponent
            url={url}
            onClick={() => handleOpenImageModal(url)}
          />
        );
      },
    },
    {
      id: 'createdAt',
      label: t('common.date'),
      width: 205,
      render: (val) => <>{new Date(val).toLocaleString('pt-BR')}</>,
    },
  ];

  const onResponseStream = () => {
    mutateGetAllFaceRecognitionEventByLicenseCount().then(setEventCount);
  };

  const handleOpenImageModal = (imageUrl: string) => {
    setIsVisibleImageModal(true);
    setImageUrlToRender(imageUrl);
  };

  const refetchEvents = async () => {
    await getStreamByEndpointAsync(
      FACE_RECOGNITION_EVENT_ENDPOINT,
      setEvents,
      pagination,
      onResponseStream,
      setIsShowLoadingComponent,
      pausedUpdateParams,
    );
  };

  useEffect(() => {
    setIsShowLoadingComponent(true);
    refetchEvents();
  }, [pagination, pausedUpdateParams.isPaused]);

  return (
    <Box>
      <TableComponent
        columns={columns}
        totalCount={eventCount}
        rows={events}
        onChangePageOrRowsPerPage={setPagination}
        additionalTitleComponent={
          <PauseButtonComponent
            pausedUpdateParams={pausedUpdateParams}
            setPausedUpdateParams={setPausedUpdateParams}
          />
        }
        sortByName={'createdAt'}
        title={t('monitoring.eventsFacial')}
      />

      <ImageModalComponent
        isOpen={isVisibleImageModal}
        handleClose={() => setIsVisibleImageModal(false)}
        imageUrl={imageUrlToRender}
      />
    </Box>
  );
};

export default FaceEventsListPage;
